import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { CustomStore } from '../../stores/CustomStore';
import { ProjectManagerStore } from '../../stores/ProjectManagerStore';
import { AppDispatcher } from '../../dispatcher/AppDispatcher';
import { ActionConstants } from '../../constants/ActionConstants';
import { UIHelper } from '../../utils/UIHelper';
import { UICoreButton } from '../UICoreButton';
import { InputBox } from '../InputBox';
import { InputCheckBox } from '../InputCheckBox';
import { DesktopSelect } from '../DesktopSelect';
import { MAIL_CONTENT_EN, MAIL_CONTENT_DE } from '../../constants/TextConstants';
import { PersistentStore } from '../../stores/PersistentStore';
import { ShareBaseTab } from './ShareBaseTab';

export enum FormularItemType {
    HIDDEN = 0,
    TEXT = 1,
    CHECK = 2,
    MULTILINE = 3,
    SELECT = 4
}

export interface FormularItem {
    label: string;
    type: FormularItemType;
    required: boolean;
    key: string;
    defaultValue: any;
    name?: string;
    values?: Array<{label: string, value: string}>;
}

interface ShareFormularState {
    valid: boolean;
}

export class ShareFormular extends ShareBaseTab<ShareFormularState> {
    protected values: {[key: string]: string | number | boolean} = {};
    protected config: Array<FormularItem> = [
        {
            type: FormularItemType.TEXT, label: t('Project name'), required: true, key: 'project-name',
            defaultValue: ProjectManagerStore.getCurrentProjectName(t('Unnamed'))
        },
        {type: FormularItemType.TEXT, label: t('Title'), required: false, key: 'title', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('First name'), required: false, key: 'first-name', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Last name'), required: false, key: 'last-name', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Zip code'), required: true, key: 'zip-code', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('City'), required: true, key: 'town', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Country'), required: true, key: 'country', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Phone'), required: true, key: 'phone', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Email'), required: true, key: 'email', defaultValue: null},

        {type: FormularItemType.TEXT, label: t('Company'), required: false, key: 'company', defaultValue: null},
        {type: FormularItemType.TEXT, label: t('Department'), required: false, key: 'department', defaultValue: null},
        {
            type: FormularItemType.CHECK, label: t('I agree to be contacted for further information / consulting'),
            required: true, key: 'accept', defaultValue: true
        },
    ];

    public componentWillMount(): void {
        this.values = CustomStore.getCustomState('shareformular');
        this.validate();
    }

    public componentWillUnmount(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SAVE_DATA,
            value: {
                key: 'shareformular',
                payload: this.values
            }
        });
    }

    public componentDidMount(): void {
        if (this.props.visible) {
            UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element);
        }
    }

    public componentDidUpdate(): void {
        if (this.props.visible) {
            UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element);
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.visible) {
            return null;
        }
        return (
            <div
                className="sidebar-tab-container"
                key={'mail-form-tab-' + this.props.layoutType.toString()}
            >
                <div className="scrollbar-wcf">
                    <div className="sidebar-content standard-header">
                        {this.header(t('Contact'))}
                        {this.elements()}
                        {this.button()}
                        <div className="required-info">*&nbsp;{t('required')}</div>
                    </div>
                </div>
            </div>
        );
    }

    protected button = (): JSX.Element => {
        if (this.state.valid) {
            return (
                <UICoreButton color="green" align="block" onClick={this.submit} label={t('Send e-mail')}/>
            );
        }

        return (
            <UICoreButton color="light-grey" align="block" label={t('Send e-mail')}/>
        );
    };

    protected setValue(value: string | number | boolean, key: string, forceUpdate?: boolean): void {
        this.values[key] = value;
        this.validate(forceUpdate);
    }

    protected validate(forceUpdate?: boolean): void {
        let valid: boolean = true;
        this.config.forEach((item) => {
            if (item.required && !this.values[item.key]) {
                    valid = false;
                    return;
                }
        });
        if (this.state == null || this.state.valid !== valid || forceUpdate) {
            this.setState({valid: valid});
        }
    }

    protected elements = (): Array<JSX.Element> => {
        const result: Array<JSX.Element> = [];
        this.config.map((item: FormularItem, index: number) => {
            switch (item.type) {
                case FormularItemType.TEXT:
                    result.push(
                        <InputBox
                            placeholder={item.label + (item.required ? ' *' : '')}
                            className="mail-input"
                            key={index.toString() + '-item'}
                            ref={item.key}
                            changeCallback={(value) => { this.setValue(value, item.key); }}
                            defaultValue={this.values[item.key] != null ? this.values[item.key] : item.defaultValue}
                            name={item.name}
                            position="input-container-sidebartab"
                        />
                    );
                    break;
                case FormularItemType.MULTILINE:
                    result.push(
                        <InputBox
                            placeholder={item.label + (item.required ? ' *' : '')}
                            className="mail-input"
                            key={index.toString() + '-item'}
                            ref={item.key}
                            changeCallback={(value) => { this.setValue(value, item.key); }}
                            defaultValue={this.values[item.key] != null ? this.values[item.key] : item.defaultValue}
                            lines={4}
                            noX={true}
                            name={item.name}
                            position="input-container-sidebartab"
                        />
                    );
                    break;
                case FormularItemType.CHECK:
                    result.push(
                        <InputCheckBox
                            label={item.label + (item.required ? ' *' : '')}
                            active={this.values[item.key] === true}
                            onClick={() => { this.setValue(this.values[item.key] == null || this.values[item.key] === false, item.key, true); }}
                        />
                    );
                    break;
                case FormularItemType.SELECT:
                    const selectValue: string = this.values[item.key] != null ? this.values[item.key] : item.defaultValue;
                    result.push(
                        <DesktopSelect
                            className={'input-group-formular' + (selectValue != null ? ' input-group-valid' : ' input-group-invalid')}
                            options={item.values != null ? item.values.map((value) => {
                                return {
                                    name: value.label,
                                    className: 'egr-select-option-noicon',
                                    value: value.value,
                                    callback: () => { this.setValue(value.value, item.key, true); }
                                };
                            }).filter((option) => {
                                return option.value != null || selectValue == null;
                            }) : []}
                            title=""
                            value={selectValue}
                            id={item.key}
                        />
                    );
                    break;
                default:
                    break;
            }
        });
        return result;
    };

    /**
     * this functions should be changes in customer branches
     */
    protected submit = (): void => {
        let customerMail: string = MAIL_CONTENT_EN;
        let manufacturerMail: string = MAIL_CONTENT_EN + '\n';
        if (PersistentStore.getState().language === 'de') {
            customerMail = MAIL_CONTENT_DE;
            manufacturerMail = MAIL_CONTENT_DE + '\n';
        }
        for (const key in this.values) {
            if (this.values[key] != null) {
                manufacturerMail += '\n' + key + ':' + (this.values[key] ? this.values[key] : '');
            }
        }

        // tslint:disable-next-line:no-console
        console.log(customerMail + manufacturerMail);
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/ShareFormular.tsx